import { Badge } from "@package/subframe/components/Badge";
import { format } from "date-fns";

import type { LogEntryResponseDto } from "@/generated/model";

interface LogEntryCardProps {
  entry: LogEntryResponseDto;
}

export default function LogEntryCard({ entry }: LogEntryCardProps) {
  const recordedByName =
    [entry.recordedBy.firstName, entry.recordedBy.lastName]
      .filter(Boolean)
      .join(" ") || entry.recordedBy.email;

  const data = entry.data as Record<string, unknown>;

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-neutral-200 px-4 py-3">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <span className="text-caption font-caption text-subtext-color">
          {format(new Date(entry.timestamp), "HH:mm")} &middot; {recordedByName}
        </span>
        <div className="flex items-center gap-2">
          {entry.room && <Badge variant="neutral">{entry.room.name}</Badge>}
          {entry.fireAlarmCallPoint?.name && (
            <Badge variant="neutral">
              Call point: {entry.fireAlarmCallPoint.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Data fields */}
      <div className="flex w-full flex-wrap gap-x-4 gap-y-1">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center gap-1">
            <span className="text-caption font-caption text-subtext-color capitalize">
              {key.replace(/([A-Z])/g, " $1").trim()}:
            </span>
            <span className="text-caption font-caption-bold text-default-font">
              {typeof value === "boolean"
                ? value
                  ? "Yes"
                  : "No"
                : String(value ?? "-")}
            </span>
          </div>
        ))}
      </div>

      {/* Notes */}
      {entry.notes && (
        <span className="text-caption font-caption text-subtext-color italic">
          {entry.notes}
        </span>
      )}
    </div>
  );
}
